import { useEffect, useState } from "react";

import { useCan } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, PIN_STATUSES, STATUS_IDENTIFIERS, WARRANTY_TYPES } from "models";
import { INTERVENTION_TABS } from "modules/AppointmentDetails/components/Interventions/components";
import { useAppendPinMutation, useDeleteInterventionMutation, useDeletePinMutation } from "modules/AppointmentDetails/components/Interventions/hooks";
import { setConnectedInterventionIdToFilter, setConnectedQuestionResultIdToFilter, useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { isCarReadyOrQualityCheck, isWorkStarted } from "utils/appointmentUtils";

import type { Appointment, Intervention, PIN_TYPES } from "models";
import type { GeneralInfoFormValues } from "modules/AppointmentDetails/components/Interventions/components/GeneralInfo/components";
import type { FormEvent } from "react";

type GeneralInfoProps = {
  intervention: Intervention;
  appointment: Appointment | null;
  onPinTypeSet: (pinTypeID: PIN_TYPES | null) => void;
  typeID: PIN_TYPES | null;
  handleTabSelection: (tab: INTERVENTION_TABS) => void;
  onSetHasChanges: (hasChanges: boolean) => void;
};

const FormHelper = createFormHelper<GeneralInfoFormValues>();

export const useGeneralInfo = ({ intervention, appointment, onPinTypeSet, typeID, onSetHasChanges, handleTabSelection }: GeneralInfoProps) => {
  const {
    setValue,
    reset,
    formState: { isDirty, isSubmitted, isSubmitSuccessful }
  } = FormHelper.useFormContext();
  const title = FormHelper.useWatch("title");
  const customerOk = FormHelper.useWatch("customer_ok");

  const { user } = usePermanentRealTimeUser();
  const selectedLocation = useAppointmentLocation();
  const { mutateAsync: appendPin } = useAppendPinMutation();
  const { mutateAsync: deletePin } = useDeletePinMutation();
  const { mutate: deleteIntervention, isPending: isDeleteInterventionPending } = useDeleteInterventionMutation();
  const canCreateFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);
  const canUpdateInterventions = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Interventions);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [pinTypeID, setPinTypeID] = useState<PIN_TYPES | null>(typeID);
  const [showDeleteInterventionModal, setShowDeleteInterventionModal] = useState(false);

  const isDisabled = appointment?.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus || !canUpdateInterventions;
  const followDMS = !intervention.is_local && selectedLocation?.keep_importing_after_work_started && isWorkStarted(appointment?.appointment_status_identifier);

  const cannotToggleCustomerOK = intervention.mechanic_fixed || Boolean(appointment && isCarReadyOrQualityCheck(appointment.appointment_status_identifier));

  const toggleDropdown = () => setOpenDropdown(prev => !prev);
  const handleDeleteLocalIntervention = () => setShowDeleteInterventionModal(true);
  const handleCancelDeleteLocalIntervention = () => setShowDeleteInterventionModal(false);

  const toggleCustomerOk = () => setValue("customer_ok", !customerOk, { shouldDirty: true });

  const handlePinUpdate = async (pinTypeID: PIN_TYPES | null) => {
    setPinTypeID(pinTypeID);
    onPinTypeSet(pinTypeID);

    if (pinTypeID === null) {
      await deletePin({ intervention_id: intervention.id, note: "" });
      return;
    }

    const pin = {
      pin_type_id: pinTypeID,
      warranty_type_id: WARRANTY_TYPES.NotSet,
      pin_status_id: PIN_STATUSES.NotSet,
      intervention_id: intervention.id,
      appointment_id: intervention.appointment_id,
      order_status: null,
      dealer_location_id: selectedLocation?.id ?? -1,
      car_id: intervention.car_id ?? -1,
      user_id: user ? user.id : -1,
      claim_nr: "",
      ref_nr: "",
      support_nr: "",
      note: "",
      visible_important_items: false,
      keep_parts: false,
      recalls_id: "",
      pon_recall_id: "",
      rdw_recall_id: ""
    };

    if (canCreateFullPin) await appendPin(pin);
    handleTabSelection(INTERVENTION_TABS.PinType);
  };

  const handleDeleteLocalInterventionConfirm = () => {
    deleteIntervention(intervention.id);
    setShowDeleteInterventionModal(false);
    setConnectedInterventionIdToFilter(null);
    setConnectedQuestionResultIdToFilter(null);
  };

  const handleButtonGroupClick = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    toggleDropdown();
  };

  useEffect(() => {
    onSetHasChanges(isDirty);
    if (isSubmitSuccessful && title) reset();
  }, [isDirty, isSubmitSuccessful]);

  return {
    title,
    isDirty,
    isSubmitted,
    pinTypeID,
    followDMS,
    isDisabled,
    customerOk,
    openDropdown,
    isDeleteInterventionPending,
    canUpdateInterventions,
    cannotToggleCustomerOK,
    showDeleteInterventionModal,
    toggleDropdown,
    handlePinUpdate,
    toggleCustomerOk,
    handleButtonGroupClick,
    handleDeleteLocalIntervention,
    handleCancelDeleteLocalIntervention,
    handleDeleteLocalInterventionConfirm
  };
};
